import { useState } from 'react'
import type { Member } from '@domain/state.ts'
import type { Reminder } from '@domain/rules/pending.ts'
import { es } from '../strings/es.ts'

/**
 * A qué horas se le recuerda a un niño lo que toca.
 *
 * Está aquí, en los ajustes de cada hijo, y no en el estado del sistema, porque
 * es un acuerdo de la casa y no una propiedad del aparato: la hora de acostarse
 * de Gabriel no es la misma que la de su hermano, y ninguna de las dos depende
 * de qué tablet tengan en la mano.
 *
 * Y lo que estas horas encienden es sobre todo el aviso **en la pantalla**. El
 * que manda el servidor es una copia de fuera que se apunta aparte y que
 * necesita red; este funciona en modo avión, que es donde vive la app.
 */
export function RemindersSetting({
  member,
  onSave,
}: {
  member: Member
  onSave: (avisos: readonly Reminder[]) => void
}) {
  const [horas, setHoras] = useState<Reminder[]>([...member.reminders])
  const [saved, setSaved] = useState(false)

  const save = (): void => {
    onSave(horas)
    setSaved(true)
    setTimeout(() => {
      setSaved(false)
    }, 2000)
  }

  return (
    <section className="border-rule mt-6 border-t pt-5">
      <h3 className="m-0 mb-1 text-base font-extrabold">{es.config.recordar}</h3>
      <p className="text-muted mt-0 mb-4 text-sm">{es.config.recordarHelp}</p>

      {horas.length === 0 ? (
        <p className="text-muted mt-0 mb-3 text-sm font-bold">{es.config.recordarNinguna}</p>
      ) : null}

      <ul className="m-0 mb-3 grid list-none gap-3 p-0">
        {horas.map((aviso, index) => (
          // La lista es corta y posicional; su índice es la identidad que ve un
          // adulto, y eso es lo que hace correcta aquí una clave por índice.
          <li key={index} className="border-rule m-0 rounded-xl border p-3">
            <div className="flex items-center gap-3">
              <input
                type="time"
                value={aviso.hora}
                aria-label={es.config.recordar}
                onChange={(e) => {
                  setHoras(horas.map((h, i) => (i === index ? { ...h, hora: e.target.value } : h)))
                }}
                className="border-rule bg-surface rounded-lg border-2 p-2 text-lg font-bold"
              />
              <span className="flex-1" />
              <button
                type="button"
                aria-label={es.config.recordarQuitar}
                onClick={() => {
                  setHoras(horas.filter((_, i) => i !== index))
                }}
                className="border-rule rounded-lg border-2 px-4 py-2 font-bold"
              >
                ✕
              </button>
            </div>
            <label className="mt-3 flex items-center gap-3 text-sm font-bold">
              <input
                type="checkbox"
                className="h-5 w-5"
                checked={aviso.previo}
                onChange={(e) => {
                  setHoras(
                    horas.map((h, i) => (i === index ? { ...h, previo: e.target.checked } : h)),
                  )
                }}
              />
              {es.config.recordarPrevio}
            </label>
          </li>
        ))}
      </ul>

      <button
        type="button"
        disabled={horas.length >= 6}
        onClick={() => {
          setHoras([...horas, { hora: '21:00', previo: true }])
        }}
        className="text-teal mb-4 block text-sm font-bold underline underline-offset-4 disabled:opacity-40"
      >
        {es.config.recordarAdd}
      </button>

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={save}
          className="bg-teal rounded-lg px-5 py-3 font-bold text-white"
        >
          {es.config.recordarSave}
        </button>
        {saved ? (
          <span role="status" className="text-mint text-sm font-bold">
            {es.config.saved}
          </span>
        ) : null}
      </div>
    </section>
  )
}
